<template>
  <div class="base-dropdown">
    <div
      ref="triggerRef"
      class="base-dropdown__trigger"
      role="button"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      :aria-controls="menuId"
      @click="handleToggle"
    >
      <slot name="trigger" />
    </div>

    <Teleport to="body">
      <Transition
        name="dropdown"
        @enter="onEnter"
        @leave="onLeave"
      >
        <div
          v-if="isOpen"
          ref="menuRef"
          :id="menuId"
          class="base-dropdown__menu"
          role="menu"
        >
          <slot name="content" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { onClickOutside } from '@vueuse/core'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: () => `dropdown-${Math.random().toString(36).substr(2, 9)}`
  }
});

const emit = defineEmits(['toggle']);

const triggerRef = ref(null);
const menuRef = ref(null);
const menuId = ref(props.id);

onClickOutside(menuRef, (e) => {
    if (!props.isOpen) return;

    emit('toggle', false)
},
    { ignore: [triggerRef] }
)

// Create a custom event for dropdown state changes
const DROPDOWN_STATE_CHANGE = 'dropdown-state-change';

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const menu = document.getElementById(menuId.value);
  if (!menu) return;

  if (window.innerWidth <= 640) {
    menu.style.top = `${rect.bottom + 8}px`;
    menu.style.left = '20px';
    menu.style.right = '20px';
  } else {
    menu.style.top = `${rect.bottom + 8}px`;
    menu.style.right = `${document.documentElement.clientWidth - rect.right}px`;
  }
};

const handleToggle = (event) => {
  event.stopPropagation();
  const newState = !props.isOpen;

  // Emit global event when opening dropdown
  if (newState) {
    window.dispatchEvent(new CustomEvent(DROPDOWN_STATE_CHANGE, {
      detail: { openedId: menuId.value }
    }));
  }

  emit('toggle', newState);
};

// Listen for other dropdowns opening
const handleOtherDropdownOpen = (event) => {
  const { openedId } = event.detail;
  if (openedId !== menuId.value && props.isOpen) {
    emit('toggle', false);
  }
};

watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    nextTick(updateDropdownPosition);
  }
});

const onEnter = (el) => {
  el.style.height = '0';
  requestAnimationFrame(() => {
    el.style.height = el.scrollHeight + 'px';
  });
};

const onLeave = (el) => {
  el.style.height = '0';
};

onMounted(() => {
  window.addEventListener('resize', updateDropdownPosition);
  window.addEventListener('scroll', updateDropdownPosition);
  window.addEventListener(DROPDOWN_STATE_CHANGE, handleOtherDropdownOpen);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition);
  window.removeEventListener(DROPDOWN_STATE_CHANGE, handleOtherDropdownOpen);
});
</script>

<style lang="scss" scoped>
.base-dropdown {
  position: relative;
  width: 100%;
  height: 100%;

  &__trigger {
    cursor: pointer;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__menu {
    position: fixed;
    min-width: 200px;
    padding: 8px 0;
    box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.15), 0px 4px 6px 2px rgba(0, 0, 0, 0.10);
    background-color: white;
    border-radius: 6px;
    overflow: hidden;
    z-index: 9999;
    transform-origin: top right;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
